/**
 * Gas deliverability — the SANCTIONED Dth/day → net-electrical-MW converter.
 *
 * Nothing in the platform converted the gas axis (Dth/day end-to-end) into
 * MW before this module; the conversion needs a HEAT RATE, and an implied or
 * assumed heat rate must be labeled, never silent. This is a NEW LEAF module
 * on purpose: `gasInterconnectDerate.ts` is byte-hash-sensitive to the
 * publish-gate replay and must not grow — import from it, never into it.
 *
 * DIMENSIONAL CHAIN (all constants named, no magic numbers):
 *
 *   dthPerDay [Dth/day]
 *     × MMBTU_PER_DTH  [MMBtu/Dth]   → mmbtuPerDay  [MMBtu/day]   (exact ≡ 1)
 *     ÷ HOURS_PER_DAY  [h/day]       → mmbtuPerHour [MMBtu/h]
 *     ÷ heatRateMmbtuPerMwh [MMBtu/MWh] → MWh/h ≡ MW (net electrical)
 *
 *   heatRateMmbtuPerMwh = heatRateBtuPerKwh × KWH_PER_MWH ÷ BTU_PER_MMBTU
 *                       = heatRateBtuPerKwh ÷ 1000
 *
 * Hand check: 100,000 Dth/day @ 9.0 MMBtu/MWh → 100000 / 24 / 9 = 462.963 MW.
 *
 * HEAT-RATE PRECEDENCE (strict; each rung labels itself):
 *   1. 'scenario-implied'  — the promoted scenario's own frozen solve:
 *      result.gas.peakMmbtuPerHour ÷ result.capacity.normalNetMW
 *      (MMBtu/h ÷ MW = MMBtu/MWh, NET — the same implied heat rate
 *      screeningEconomics derives per scenario).
 *   2. 'equipment-rating'  — max unit HHV heat rate across the scenario's
 *      gas-fired units ÷ 1000. Max = CONSERVATIVE: the worst unit extracts
 *      the fewest MW per Dth, so the deliverable-MW cap is lowest.
 *   3. 'planning-default'  — DEFAULT_GAS_HEAT_RATE_BTU_PER_KWH (9,000)
 *      ÷ 1000 = 9.0 MMBtu/MWh, disclosed as an assumed screening figure.
 */
import type { GenerationImpactScenarioSnapshot } from '../../types/generationImpact';
import { DEFAULT_GAS_HEAT_RATE_BTU_PER_KWH } from '../groundGasFuelCost';

/** 1 Dth ≡ 10 therms ≡ 1,000,000 Btu ≡ 1 MMBtu — an exact identity. */
export const MMBTU_PER_DTH = 1;
export const HOURS_PER_DAY = 24;
export const KWH_PER_MWH = 1_000;
export const BTU_PER_MMBTU = 1_000_000;

/** Gas-fired technologies that draw pipeline gas (the shared fuel pool). */
const GAS_FIRED_TECHNOLOGIES = new Set([
  'reciprocating-engine',
  'simple-cycle-gas-turbine',
  'combined-cycle-block',
]);

export interface GasHeatRateResolution {
  mmbtuPerMwh: number;
  basis: 'scenario-implied' | 'equipment-rating' | 'planning-default';
  /** Human-readable provenance for disclosures. */
  citation: string;
  disclosures: string[];
}

/** Resolve the heat rate to convert firm gas into deliverable MW. */
export function resolveGasHeatRate(
  scenario?: GenerationImpactScenarioSnapshot | null,
): GasHeatRateResolution {
  const peak = scenario?.result?.gas?.peakMmbtuPerHour;
  const netMW = scenario?.result?.capacity?.normalNetMW;
  if (
    typeof peak === 'number' && Number.isFinite(peak) && peak > 0 &&
    typeof netMW === 'number' && Number.isFinite(netMW) && netMW > 0
  ) {
    const implied = peak / netMW; // MMBtu/h ÷ MW = MMBtu/MWh (net)
    return {
      mmbtuPerMwh: implied,
      basis: 'scenario-implied',
      citation: `promoted scenario '${scenario!.name}' — peak fuel ${peak} MMBtu/h ÷ ${netMW} MW net`,
      disclosures: [
        `Heat rate ${round3(implied)} MMBtu/MWh implied by the promoted scenario's frozen solve (net basis).`,
      ],
    };
  }
  const gasUnits = (scenario?.input?.units ?? []).filter(u =>
    GAS_FIRED_TECHNOLOGIES.has(u.technology),
  );
  const rated = gasUnits
    .map(u => u.heatRateBtuPerKWhHhv)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0);
  if (rated.length > 0) {
    const worstBtuPerKwh = Math.max(...rated); // conservative: fewest MW per Dth
    return {
      mmbtuPerMwh: worstBtuPerKwh * KWH_PER_MWH / BTU_PER_MMBTU,
      basis: 'equipment-rating',
      citation: `worst gas-fired unit HHV heat rate ${worstBtuPerKwh} Btu/kWh across the promoted scenario`,
      disclosures: [
        `Heat rate ${round3(worstBtuPerKwh / 1000)} MMBtu/MWh from the worst-rated gas unit (conservative — lowest MW per Dth).`,
      ],
    };
  }
  return {
    mmbtuPerMwh: DEFAULT_GAS_HEAT_RATE_BTU_PER_KWH * KWH_PER_MWH / BTU_PER_MMBTU,
    basis: 'planning-default',
    citation: `assumed screening genset heat rate ${DEFAULT_GAS_HEAT_RATE_BTU_PER_KWH} Btu/kWh`,
    disclosures: [
      `Heat rate ${DEFAULT_GAS_HEAT_RATE_BTU_PER_KWH / 1000} MMBtu/MWh is an ASSUMED screening genset figure — not equipment data.`,
    ],
  };
}

/**
 * Firm gas → deliverable net electrical MW. Null (never NaN, never 0) on a
 * non-finite / non-positive input — an absent capacity must stay absent.
 */
export function dthPerDayToDeliverableMW(
  dthPerDay: number,
  heatRate: GasHeatRateResolution,
): number | null {
  if (!Number.isFinite(dthPerDay) || dthPerDay <= 0) return null;
  if (!Number.isFinite(heatRate.mmbtuPerMwh) || heatRate.mmbtuPerMwh <= 0) return null;
  const mmbtuPerDay = dthPerDay * MMBTU_PER_DTH;
  const mmbtuPerHour = mmbtuPerDay / HOURS_PER_DAY;
  return mmbtuPerHour / heatRate.mmbtuPerMwh; // MMBtu/h ÷ MMBtu/MWh = MW
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}
